import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

// Principals (operating team), then Chairman set apart.
const PRINCIPALS = [
  {
    name: "Marc Abourjeily",
    role: "Co Founder and Managing Partner",
    portrait: "/images/portrait-marc.jpg",
    bio:
      "Marc Abourjeily is the Co Founder and Managing Partner of ZT Global, with over 22 years of global investment banking experience, including 17 years at bulge-bracket institutions across Dubai, London and New York. He has held roles at Goldman Sachs, Merrill Lynch and HSBC, advising some of the world's most sophisticated sovereign, corporate and financial institution clients at the C-suite level. His most recent bulge bracket role was Managing Director and Co-Head of Capital Markets for the Middle East, North Africa and Turkey at HSBC, a position he held until 2021.",
    bio2:
      "During his tenure, Marc also ran HSBC's Debt Capital Markets franchise, widely regarded as the premier platform in the MENAT region, which ranked #1 in league tables for ten consecutive years. His expertise spans the full financing spectrum, including loan syndications, equity capital markets, equity derivatives, risk management, ratings advisory, bonds, project bonds, private placements and most recently the full spectrum in high growth ventures. Over his career, he has led or participated in more than US$500 billion in debt issuances globally.",
    bio4:
      "Marc holds a bachelor's degree in Finance with a minor in Mathematics from McGill University.",
    metrics: [
      { v: "22+", l: "Years" },
      { v: "$500B+", l: "Transactions" },
    ],
  },
  {
    name: "Karim Jaroudi",
    role: "Co Founder and Managing Partner",
    portrait: "/images/portrait-karim.jpg",
    bio:
      "Karim Jaroudi is Co Founder and Managing Partner of ZT Global, with over 20 years of international investment banking, structured finance and strategic advisory experience across New York, London, Singapore and Dubai.",
    bio2:
      "He has held senior Managing Director roles at SMBC, where he served as Head of CIB Coverage for the Middle East and led Corporate and Structured Finance activities across the region, overseeing advisory, financing and capital markets coverage across sovereigns, financial institutions, government related entities and large corporates.",
    bio3:
      "Karim has advised on and led landmark cross border financing and strategic transactions across the GCC, Asia and international markets, working closely with leading sovereign wealth funds, national oil companies, ministries, central banks and C suite leadership teams. During his tenure at SMBC, he played a key role in expanding institutional capital flows and financing connectivity between Asia, Japan and the GCC across syndicated loans, structured financing, private placements and capital markets transactions.",
    bio4:
      "Prior to SMBC, Karim held roles at Jefferies, Merrill Lynch and HSBC, and also worked in principal investing in New York at GEMNY Capital. His transaction experience spans more than US$250 billion across structured finance, M&A, project finance, syndicated loans and equity and debt capital markets.",
    bio5:
      "Karim holds an MBA from NYU Stern, a Master's degree from HEC Paris and a Bachelor's degree in Business and Finance from the American University of Beirut.",
    metrics: [
      { v: "20+", l: "Years" },
      { v: "$250B+", l: "Transactions" },
    ],
  },
  {
    name: "Tariq S. Khan",
    role: "Managing Director and Chief Operating Officer",
    portrait: "/images/portrait-tariq.jpg",
    bio:
      "Tariq brings over 30 years of experience in corporate finance, public and private equity, capital formation and operating platforms across real estate, healthcare, consumer goods, technology, industrials and retail.",
    metrics: [
      { v: "30+", l: "Years Experience" },
    ],
  },
];

const CHAIRMAN = {
  name: "Taseer Badar",
  role: "Chairman and Founder, ZT Global and ZT Corporate",
  portrait: "/images/portrait-taseer.jpg",
  bio:
    "Taseer is the Chairman and Founder of ZT Global and ZT Corporate, with nearly three decades of principal investing and operating experience across healthcare, automotive, consumer, financial services and youth sports. Managing approximately US$2 billion in annual revenues across a multi-vertical portfolio, he anchors ZT Global's long-term capital philosophy and strategic direction.",
};

const Profile = ({ p, eyebrow }) => (
  <Reveal
    className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12"
    data-testid={`leader-${p.name.split(" ")[0].toLowerCase()}`}
  >
    <div className="md:col-span-4">
      <p className="label-zt text-gold-600">{eyebrow}</p>
      <div className="hairline-gold mt-3" />
      <h2 className="mt-8 font-serif text-4xl md:text-5xl text-navy tracking-tight leading-[1.05]">
        {p.name}
      </h2>
      <p className="mt-4 font-sans text-sm uppercase tracking-[0.25em] text-navy/60">
        {p.role}
      </p>
      {p.metrics && (
        <div className="mt-10 grid grid-cols-2 gap-6 max-w-xs">
          {p.metrics.map((m) => (
            <div key={m.l}>
              <p className="font-serif text-3xl text-navy">{m.v}</p>
              <p className="mt-2 label-zt text-gold-600">{m.l}</p>
            </div>
          ))}
        </div>
      )}
    </div>
    <div className="md:col-span-8 md:pt-1">
      <p className="text-lg leading-relaxed text-navy/80 font-light">{p.bio}</p>
      {p.bio2 && <p className="mt-5 text-lg leading-relaxed text-navy/80 font-light">{p.bio2}</p>}
      {p.bio3 && <p className="mt-5 text-lg leading-relaxed text-navy/80 font-light">{p.bio3}</p>}
      {p.bio4 && <p className="mt-5 text-lg leading-relaxed text-navy/80 font-light">{p.bio4}</p>}
      {p.bio5 && <p className="mt-5 text-lg leading-relaxed text-navy/80 font-light">{p.bio5}</p>}
    </div>
  </Reveal>
);

const Leadership = () => {
  return (
    <>
      <section
        className="bg-ivory text-navy pt-44 pb-24 md:pt-52 md:pb-32 relative overflow-hidden border-b border-navy/10"
        data-testid="leadership-header"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(200,169,97,0.10),transparent_55%)]" />
        <div className="container-zt relative">
          <Reveal>
            <p className="label-zt text-gold-600">The People</p>
            <div className="h-px w-12 bg-gold-600 mt-3" />
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-8 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-4xl text-navy">
              Our <span className="italic text-gold-600">Leadership</span>.
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 max-w-2xl text-lg text-navy/75 font-light leading-relaxed">
              Senior investment bankers and an owner-operator chairman — anchoring ZT
              Global's commitment to disciplined structuring, cross border execution and
              long term capital deployment.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Chairman & Founder — leads the page */}
      <section className="bg-ivory py-24 md:py-32" data-testid="leadership-chairman">
        <div className="container-zt mb-16">
          <Reveal>
            <p className="label-zt text-gold-600">Chairman & Founder</p>
            <div className="hairline-gold mt-3" />
          </Reveal>
        </div>
        <div className="container-zt">
          <Profile p={CHAIRMAN} eyebrow="Group Level — ZT Global & ZT Corporate" />
        </div>
      </section>

      {/* Divider between Chairman and Principals */}
      <section className="bg-ivory" data-testid="leadership-divider">
        <div className="container-zt py-12">
          <div className="border-t border-navy/15" />
        </div>
      </section>

      {/* Principals — Marc, Karim, Tariq (alphabetical), no numbering */}
      <section className="bg-ivory py-12 md:py-16 pb-24 md:pb-32" data-testid="leadership-principals">
        <div className="container-zt mb-16">
          <Reveal>
            <p className="label-zt text-gold-600">Principals & Operating Team</p>
            <div className="hairline-gold mt-3" />
          </Reveal>
        </div>
        <div className="container-zt space-y-24 md:space-y-32">
          {PRINCIPALS.map((p) => (
            <Profile key={p.name} p={p} eyebrow="Principal" />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-24 md:py-32 border-t border-navy/10" data-testid="leadership-cta">
        <div className="container-zt grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <p className="label-zt text-gold-600">Partner with ZT Global</p>
            <div className="hairline-gold mt-3" />
            <h2 className="mt-8 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-navy">
              Speak directly with a <span className="italic text-gold-600">principal</span>.
            </h2>
          </Reveal>
          <Reveal delay={150} className="md:col-span-4 md:pb-2">
            <Link to="/contact" className="btn-navy" data-testid="leadership-cta-contact">
              Begin a Conversation <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Leadership;
